// Copyright (c) Docugami, Inc. All rights reserved.

import { TextField, ITextFieldStyles } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { LargeHeading, LargeUI, SmallContent, userFontFamily } from "components/common/Fonts";
import { useKeyPressHandler } from "hooks/KeyPressHandler";
import React, { useEffect } from "react";
import styled from "styled-components";
import { TelemetryPageView } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { aaTextColor, emptyColor, emptyGray, backgroundGray } from 'components/shell/presentation/AppStyles';
import { useNavigationService } from '../../../hooks/navigationService';

interface StepProps {
    canAdvance: boolean;
    userName?: string;
}

interface NameOrganizationStepProps extends StepProps {
    orgName: string;
    firstName: string;
    lastName: string;
    email: string;
    setOrgName: (value: string) => void;
    setFirstName: (value: string) => void;
    setLastName: (value: string) => void;
    onAdvance?: () => void;
}

export const Label = styled(SmallContent)`
    margin-top: 6px;
`;

const RootGrid = styled.div`
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-auto-rows: 50px;
    .label1 {
        grid-column: 1;
        grid-row: 1;
    }
    .textbox1 {
        grid-column: 3;
        grid-row: 1;
    }
    .label2 {
        grid-column: 1;
        grid-row: 2;
    }
    .textbox2 {
        grid-column: 3;
        grid-row: 2;
    }
    .label3 {
        grid-column: 1;
        grid-row: 3;
    }
    .textbox3 {
        grid-column: 3;
        grid-row: 3;
    }
    .label4 {
        grid-column: 1;
        grid-row: 4;
    }
    .textbox4 {
        grid-column: 3;
        grid-row: 4;
    }
`

export const NameOrganizationStep = (props: NameOrganizationStepProps): JSX.Element => {
    const styles: Partial<ITextFieldStyles> = {
        fieldGroup: [
            { width: '100%'}
        ],
        field: {
            borderBottom: '1px solid black',
            font: userFontFamily,
            fontSize: '30px',
            fontWeight: 500,
            height: 'fit-content',
        }
    }

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.CreateWorkspaceInfo);
    }, []);

    return (
        <StepBase
            title="Welcome to Docugami!"
            subtext="Let’s get your workspace set up by providing some basic information about yourself and your company."
            onNextClicked={props.onAdvance}
            nextButtonEnabled={props.canAdvance}>
            <RootGrid>
                <Label id="FirstNameTextFieldLabel" className="label1">First Name:</Label>
                <TextField
                    className="textbox1"
                    onChange={(ev, newValue) => props.setFirstName(newValue ?? '')}
                    value={props.firstName}
                    aria-labelledby="FirstNameTextFieldLabel"
                    autoComplete="off"
                    maxLength={100}
                    borderless
                    styles={styles} />
                <Label id="LastNameTextFieldLabel" className="label2">Last Name:</Label>
                <TextField
                    className="textbox2"
                    onChange={(ev, newValue) => props.setLastName(newValue ?? '')}
                    value={props.lastName}
                    aria-labelledby="LastNameTextFieldLabel"
                    autoComplete="off"
                    maxLength={100}
                    borderless
                    styles={styles}/>
                <Label id="EmailTextFieldLabel" className="label3">Email:</Label>
                <TextField
                    className="textbox3"
                    disabled
                    onChange={(ev, newValue) => {}}
                    value={props.email}
                    aria-labelledby="EmailTextFieldLabel"
                    borderless
                    styles={styles}/>
                <Label id="OrgNameTextFieldLabel" className="label4">Organization Name:</Label>
                <TextField
                    className="textbox4"
                    onChange={(ev, newValue) => props.setOrgName(newValue ?? '')}
                    aria-labelledby="OrgNameTextFieldLabel"
                    autoComplete="off"
                    borderless
                    styles={styles}
                    maxLength={100} />
            </RootGrid>
        </StepBase>
    )
}

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
    width: 100%;
    max-height: 600px;
    align-self: center;
    justify-self: center;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    width: 85%;
    align-items: center;
    justify-content: center;
`;

const ChildrenContainer = styled.div`
    min-height: 200px;
    max-height: 350px;
    overflow: auto;
`;

const ButtonContainer = styled.div`
    width: fit-content;
    margin-top: 10px;
    display: flex;
    gap: 20px;
`;

const LogoutButton = styled(DgButton)`
    color: ${aaTextColor};
    background: ${backgroundGray};

    &:hover {
        background-color: ${emptyColor};
    }

    &:active:hover {
        background-color: ${emptyGray};
    }
`;

interface StepBaseProps {
    title: string;
    subtext: string;
    children?: any;
    nextButtonEnabled?: boolean;
    onNextClicked?: () => void;
}

const StepBase = (props: StepBaseProps): JSX.Element => {
    useKeyPressHandler('Enter', () => { if (!!props.nextButtonEnabled && !!props.onNextClicked) props.onNextClicked() });
    const navService = useNavigationService();

    return (
        <Root>
            <LargeHeading style={{ textAlign: 'center' }}>{props.title}</LargeHeading>
            <LargeUI style={{ fontWeight: 300, textAlign: 'center' }}>{props.subtext}</LargeUI>
            <div style={{ height: '30px' }} />
            <Body>
                <ChildrenContainer>
                    {props.children}
                </ChildrenContainer>
                <ButtonContainer>
                    <DgButton style={{ marginTop: '10px' }} disabled={!props.nextButtonEnabled} onClick={props.onNextClicked} hidden={!props.onNextClicked}>NEXT</DgButton>
                    <LogoutButton style={{ marginTop: '10px' }} onClick={() => navService.logout()}>Log Out</LogoutButton>
                </ButtonContainer>
            </Body>
        </Root>
    )
}
